import { useSession, useCustomer } from 'hooks/useSession'
import useCustomerInvoice from 'hooks/useCustomerInvoice'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useToast } from '@ui/toasts/useToastManager'
import { useTranslation } from 'react-i18next'
import { getAge } from '@order/hooks/values/OrderValues'
import { AddressSchema } from '@ca/common'
import { DEFAULT_REGION_POLICIES } from '@ca/common'
import { InvoiceFormSchema } from 'utils/vatSchema'
import { getTextPaperStatus } from './getTextPaperStatus'
import { TextPaperStatus } from '@ui/elements/TextPaper/types'
import { CustomerDetailSection } from '@ca/common'

type HandleConfirmOptions = {
  onPoliciesRequired?: () => void
}

const useCustomerAge = () => {
  const customer = useCustomer()
  const birth_date = customer?.birth_date

  const age = birth_date ? getAge(new Date(birth_date)) : null

  const isAgeUnder = (value: number): boolean => {
    if (value == null) return false
    if (!age) return false

    return age < value
  }

  return { age, isAgeUnder }
}

export const useIsInvoiceRequired = () => {
  const { isAgeUnder } = useCustomerAge()
  return isAgeUnder(18)
}

export const useConfirmCustomer = () => {
  const info = useSession((s) => s.info)
  const customerPolicies = useSession((s) => s.customer?.policies)
  const config = useRemoteConfig()
  const regionPolicies = config?.region?.policies ?? DEFAULT_REGION_POLICIES
  const resolvedPolicies = regionPolicies.map((policy) => {
    const customerPolicy = customerPolicies?.[policy.policy_key]
    return {
      required: policy.required,
      acceptedAt:
        customerPolicy?.revision === policy.policy_revision
          ? customerPolicy.acceptedAt
          : null,
    }
  })
  const setInfo = useSession((s) => s.setInfo)
  const customer = useCustomer()
  const invoice = useCustomerInvoice()
  const { t } = useTranslation()

  const toast = useToast()

  const isVatRequired = config?.region?.vat === 'required'

  const isInvoiceRequired = useIsInvoiceRequired()
  const invoiceData = invoice.data

  const needsInvoice = (() => {
    if (!isInvoiceRequired) {
      return false
    }
    if (!invoiceData) {
      return true
    }
    const isValid = InvoiceFormSchema.safeParse(invoiceData)
    return !isValid.success
  })()

  const needsVat = isVatRequired && !customer?.address?.vat

  const needsContact = !customer?.mobile && !customer?.email && !customer?.phone
  const needsBirthDate = !customer?.birth_date

  const needsAddress = (() => {
    if (!customer?.address) {
      return true
    }
    const isValid = AddressSchema.safeParse(customer?.address)
    return !isValid.success
  })()

  const needsPersonalInfo = needsContact || needsBirthDate
  const needsPolicies = resolvedPolicies.some(
    (policy) => policy.required && !policy.acceptedAt
  )
  const shouldShowPoliciesError =
    needsPolicies && info.isCustomerDetailsConfirmed === false

  const handleConfirm = (options?: HandleConfirmOptions) => {
    if (needsPersonalInfo) {
      setInfo({ isCustomerDetailsConfirmed: false })
      toast({
        variant: 'error',
        text: t('validation:confirmation.personal'),
      })
      return false
    }
    if (needsAddress) {
      setInfo({ isCustomerDetailsConfirmed: false })
      toast({
        variant: 'error',
        text: t('validation:confirmation.address'),
      })
      return false
    }
    if (needsVat) {
      setInfo({ isCustomerDetailsConfirmed: false })
      toast({
        variant: 'error',
        text: t('validation:confirmation.vat'),
      })
      return false
    }
    if (needsInvoice) {
      setInfo({ isCustomerDetailsConfirmed: false })
      toast({
        variant: 'error',
        text: t('validation:confirmation.invoice'),
      })
      return false
    }
    if (needsPolicies) {
      setInfo({ isCustomerDetailsConfirmed: false })
      if (options?.onPoliciesRequired) {
        options?.onPoliciesRequired?.()
        return false
      }
      toast({
        variant: 'error',
        text: t('validation:confirmation.policies'),
      })
      return false
    }
    setInfo({ isCustomerDetailsConfirmed: true, savedDetailSections: undefined })
    return true
  }

  const hasAttemptedConfirm = info.isCustomerDetailsConfirmed === false
  const isConfirmed = info.isCustomerDetailsConfirmed
  const savedDetailSections = info.savedDetailSections

  const getDetailStatus = ({
    hasData,
    isValid,
    section,
  }: {
    hasData: boolean
    isValid: boolean
    section?: CustomerDetailSection
  }): TextPaperStatus =>
    getTextPaperStatus({
      isEmpty: !hasData,
      isValid,
      isConfirmed,
      hasAttemptedConfirm,
      section,
      savedDetailSections,
    })

  return {
    isInvoiceRequired,
    needsVat,
    needsInvoice,
    needsContact,
    needsBirthDate,
    invoice,
    needsPersonalInfo,
    needsAddress,
    needsPolicies,
    shouldShowPoliciesError,
    hasAttemptedConfirm,
    getDetailStatus,
    canConfirm:
      !needsInvoice &&
      !needsPersonalInfo &&
      !needsAddress &&
      !needsVat &&
      !needsPolicies,
    handleConfirm,
    isConfirmed,
  }
}
